/* cc-sticky-atc — sticky Add-to-Cart bar + variant chooser sheet (v2, 2026-10-25).
   Behavior on Dawn internals (pub-sub only — no MutationObserver, no polling):
   - Bar <- form: variantChange / optionValueSelectionChange / quantityUpdate.
   - Bar -> form: check the pill radio / set the dropdown + dispatch `change`
     (Dawn's variant pipeline, the cc-gallery-variant-sync pattern). Bar-driven
     picks stamp body[data-cc-bar-pick] so the gallery scroll and product-info's
     re-focus stay suppressed (media-gallery.js CC-EDIT, cc-gallery-variant-sync).
   - Add: click the main form's submit; Dawn's product-form owns the add, the
     drawer/toast own the post-ATC UX. The button says "Adding…" until
     product-form publishes cartUpdate, then "Added" (cartError resets it).
   - Price in the button follows variant × quantity ("Add 3 · $23.97").
   - Show/hide: IntersectionObserver on the main ATC + footer; phone bar hides
     while an outside field has focus (keyboard) and while the ATC toast shows.
   - Phone sheet lists every variant; desktop selects carry the choice.
   Breakpoint 990 (the theme's desktop line; iPads get the phone bar) — was 720 in v1, 750 in v2.0. */

(() => {
  'use strict';

  const BP = 990; /* the theme's desktop line; iPads get the phone bar */
  const bar = document.getElementById('cc-sticky-atc');
  const panel = document.getElementById('cc-sticky-panel');
  const btn = document.getElementById('cc-sticky-atc-btn');
  const productInfo = document.querySelector("product-info[id^='MainProduct']");
  if (!bar || !btn || !productInfo) return;

  /* i18n — hostname-based (es.chinchileproducts.com = Weglot proxy), the
     cc-cart-drawer recipe. Liquid first paint is Weglot's; these cover the
     strings this script swaps in. */
  const isES = location.hostname.indexOf('es.') === 0;
  const tr = (en, es) => (isES ? es : en);

  const sectionId = productInfo.dataset.section;
  const form = document.getElementById('product-form-' + sectionId);
  const mainSubmit = () => document.getElementById('ProductSubmitButton-' + sectionId);
  const formQty = () => document.getElementById('Quantity-' + sectionId);
  const variantSelects = () => productInfo.querySelector('variant-selects');
  const isDesktop = () => window.innerWidth >= BP;

  /* ---- product data (Liquid-built JSON in the snippet) ---- */
  let data = { soldOut: false, hasVariants: false, variants: [], moneyFormat: '${{amount}}', fallbackImg: null };
  try {
    data = Object.assign(data, JSON.parse(document.getElementById('cc-sticky-atc-data').textContent));
  } catch (e) {
    /* keep defaults */
  }
  const variantById = {};
  (data.variants || []).forEach((v) => (variantById[String(v.id)] = v));
  const productSoldOut = !!data.soldOut;

  const qtyInput = bar.querySelector('[data-cc-qty-input]');
  const minusBtn = bar.querySelector('[data-cc-qty-minus]');
  const plusBtn = bar.querySelector('[data-cc-qty-plus]');
  const stickyImg = bar.querySelector('.cc-sticky-atc__img');
  const variantEl = bar.querySelector('[data-cc-sticky-variant]');
  const stickyQty = bar.querySelector('[data-cc-sticky-qty]');
  const stickyNotify = bar.querySelector('[data-cc-sticky-notify]');
  const stickySelects = Array.from(bar.querySelectorAll('[data-cc-sticky-option]'));
  const btnText = bar.querySelector('[data-cc-btn-text]');
  const btnQty = bar.querySelector('[data-cc-btn-qty]');
  const btnPrice = bar.querySelector('[data-cc-btn-price]');
  const btnVerb = bar.querySelector('[data-cc-btn-verb]');
  const btnLong = bar.querySelector('[data-cc-btn-long]');
  const btnSizer = bar.querySelector('[data-cc-btn-sizer]');
  const reviewsLink = bar.querySelector('[data-cc-reviews-link]');
  const reviewsWord = bar.querySelector('[data-cc-reviews-word]');

  let isVisible = false;
  let panelOpen = false;
  let currentMode = 'atc';

  /* ========================
     Money
     ======================== */
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, data.moneyFormat);
    }
    const fmt = data.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const noDec = Math.round(cents / 100).toString();
    const comma = amount.replace('.', ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, comma);
  }

  /* ========================
     Conversion tracking (Klaviyo + GA4)
     ======================== */
  function trackEvent(name, payload) {
    payload = payload || {};
    payload.timestamp = Date.now();
    if (window.dataLayer) {
      const obj = { event: name };
      for (const k in payload) obj[k] = payload[k];
      window.dataLayer.push(obj);
    }
    if (window._learnq) {
      try {
        window._learnq.push(['track', name, payload]);
      } catch (e) {}
    }
  }

  /* Chrome Android fix: re-anchor the bar on visual viewport changes
     (toolbar collapse/expand doesn't re-position fixed elements). */
  if (window.visualViewport) {
    const updateBarPosition = () => {
      const vv = window.visualViewport;
      const visibleBottom = vv.offsetTop + vv.height;
      const layoutBottom = document.documentElement.clientHeight;
      const offset = layoutBottom - visibleBottom;
      bar.style.bottom = offset > 0 ? offset + 'px' : '0';
    };
    window.visualViewport.addEventListener('resize', updateBarPosition);
    window.visualViewport.addEventListener('scroll', updateBarPosition);
    updateBarPosition();
  }

  /* ========================
     Current variant helpers
     ======================== */
  function currentVariantId() {
    const idInput = form && form.querySelector('input[name="id"]');
    if (idInput && idInput.value) return String(idInput.value);
    try {
      return String(JSON.parse(variantSelects().querySelector('[data-selected-variant]').textContent).id);
    } catch (e) {
      return '';
    }
  }
  function currentVariant() {
    return variantById[currentVariantId()] || null;
  }
  function findVariantByOptions(values) {
    return (
      (data.variants || []).find(
        (v) => v.options.length === values.length && v.options.every((o, i) => o === values[i])
      ) || null
    );
  }
  function currentQty() {
    return qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
  }

  /* ========================
     Button label: "Add[ 3][ to cart] · $23.97" — the words are Liquid (Weglot
     translates them on first paint); only the qty and the price are rewritten.
     ======================== */
  let lastPriceCents = null;
  const SIZER_QTY = 9; /* reserve width for one qty digit + its price; the 10th tap may grow the box once */
  function setBtnLabel(variant) {
    const v = variant || currentVariant();
    if (v && typeof v.price === 'number') lastPriceCents = v.price;
    const qty = currentQty();
    if (btnQty) btnQty.textContent = qty > 1 ? ' ' + qty : '';
    if (btnPrice && lastPriceCents != null) btnPrice.textContent = formatMoney(lastPriceCents * qty);
    /* sizer = the widest near-term label in the CURRENT words (Weglot may have
       translated them) and the current breakpoint's long/short form */
    if (btnSizer && lastPriceCents != null) {
      const longOn = btnLong && getComputedStyle(btnLong).display !== 'none';
      const widest = Math.max(qty, SIZER_QTY);
      /* digits → "8": the body font's figures can be proportional ("1" is
         narrow), so the sizer must be as wide as the WIDEST digits, not the
         ones that happen to be in price × 9 */
      const wide = (str) => String(str).replace(/\d/g, '8');
      btnSizer.textContent =
        (btnVerb ? btnVerb.textContent : 'Add') +
        ' ' + wide(widest) +
        (longOn ? btnLong.textContent : '') +
        ' · ' + wide(formatMoney(lastPriceCents * widest));
    }
  }
  let resizeTick = false;
  window.addEventListener('resize', () => {
    if (resizeTick) return;
    resizeTick = true;
    requestAnimationFrame(() => { resizeTick = false; setBtnLabel(); });
  });

  /* ========================
     ATC <-> Notify Me mode (sold-out swap)
     ======================== */
  function showNotifyMode() {
    if (currentMode === 'notify') return;
    currentMode = 'notify';
    if (stickyQty) stickyQty.style.display = 'none';
    btn.style.display = 'none';
    if (stickyNotify) {
      stickyNotify.innerHTML =
        '<button type="button" class="cc-sticky-atc__btn cc-sticky-atc__btn--notify" data-cc-notify-trigger>' +
        tr('Notify me when available', 'Avísame cuando esté disponible') +
        '</button>';
      stickyNotify.style.display = '';
      stickyNotify.querySelector('[data-cc-notify-trigger]').addEventListener('click', stoqNotify);
    }
  }

  function showAtcMode() {
    if (currentMode === 'atc') return;
    currentMode = 'atc';
    if (stickyQty) stickyQty.style.display = '';
    btn.style.display = '';
    if (stickyNotify) stickyNotify.style.display = 'none';
  }

  function stoqNotify() {
    /* Stoq injects a cover button over the sold-out main ATC */
    const cover = document.querySelector('.restock-rocket-button-cover');
    if (cover) {
      cover.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      return;
    }
    const target = mainSubmit() || productInfo;
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function updateStickyBtnState(variant) {
    if (!variant) return; /* unknown combo — change nothing */
    if (variant.available === false) {
      showNotifyMode();
    } else {
      showAtcMode();
      btn.disabled = false;
      btn.style.opacity = '';
    }
  }

  /* Bar-side mirror of cc-variant-filter: for option group i>0, a value is
     selectable only if some variant pairs it with ALL earlier selected
     options (picking an impossible combo used to flicker the header). */
  function filterStickyOptions(v) {
    if (!stickySelects.length || !v || !v.options) return;
    stickySelects.forEach((sel) => {
      const idx = parseInt(sel.getAttribute('data-cc-sticky-option'), 10);
      if (idx === 0) return;
      Array.from(sel.options).forEach((opt) => {
        const ok = (data.variants || []).some(
          (cand) => cand.options[idx] === opt.value && cand.options.slice(0, idx).every((o, q) => o === v.options[q])
        );
        opt.hidden = !ok;
        opt.disabled = !ok; /* Safari belt: hidden options can still render in its popup */
      });
    });
  }

  /* ========================
     Bar <- form sync
     ======================== */
  function applyVariant(v) {
    if (!v) return;
    if (variantEl && data.hasVariants) {
      variantEl.textContent = v.title && v.title !== 'Default Title' ? v.title : '';
    }
    stickySelects.forEach((sel) => {
      const idx = parseInt(sel.getAttribute('data-cc-sticky-option'), 10);
      const value = v.options ? v.options[idx] : null;
      if (value != null && sel.value !== value) sel.value = value;
    });
    filterStickyOptions(v);
    updateStickyBtnState(v);
    setBtnLabel(v);
    if (stickyImg) {
      if (v.img) stickyImg.src = v.img;
      else if (data.fallbackImg) stickyImg.src = data.fallbackImg;
      else setTimeout(updateStickyImageFromGallery, 200);
    }
    highlightCurrentTile(String(v.id));
  }

  function updateStickyImageFromGallery() {
    if (!stickyImg) return;
    const active =
      productInfo.querySelector('media-gallery li.is-active img') ||
      productInfo.querySelector('media-gallery li[data-media-id] img');
    if (!active) return;
    const src = active.currentSrc || active.src;
    if (src) stickyImg.src = src;
  }

  function resyncFromDom() {
    const v = currentVariant();
    if (v) applyVariant(v);
  }

  /* ========================
     Add state: "Adding…" until product-form publishes cartUpdate
     ======================== */
  let addPending = false;
  let addTimer = null;
  let savedLabel = null;

  function beginAdding() {
    if (!btnText) return;
    savedLabel = btnText.innerHTML;
    addPending = true;
    btn.setAttribute('aria-busy', 'true');
    btnText.textContent = tr('Adding…', 'Agregando…');
    clearTimeout(addTimer);
    addTimer = setTimeout(restoreLabel, 15000); /* safety: never stay stuck (a slow add with the drawer's section render can take several seconds) */
  }
  function restoreLabel() {
    if (!btnText) return;
    clearTimeout(addTimer);
    addPending = false;
    btn.removeAttribute('aria-busy');
    btn.classList.remove('is-added');
    if (savedLabel != null) btnText.innerHTML = savedLabel;
    savedLabel = null;
    setBtnLabel();
  }
  function markAdded() {
    if (!addPending || !btnText) return;
    clearTimeout(addTimer);
    btn.removeAttribute('aria-busy');
    btn.classList.add('is-added');
    btnText.textContent = '✓ ' + tr('Added', 'Agregado');
    trackEvent('cc_sticky_atc_added', { quantity: currentQty() });
    addTimer = setTimeout(restoreLabel, 1600);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (e) => {
      const ev = e && e.data && e.data.variant;
      if (!ev) return;
      applyVariant(
        variantById[String(ev.id)] || {
          id: ev.id,
          title: ev.title,
          available: ev.available,
          price: typeof ev.price === 'number' ? ev.price : lastPriceCents,
          options: ev.options || [],
          media: ev.featured_media ? ev.featured_media.id : null,
          img: null,
        }
      );
    });
    /* Dawn does NOT publish variantChange when a selection resolves to a
       nonexistent combo (setUnavailable early-returns), but the picker still
       re-renders and cc-variant-filter auto-falls to a real value — resync
       after its 500/1200ms belts settle. */
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
      setTimeout(resyncFromDom, 600);
      setTimeout(resyncFromDom, 1400);
    });
    /* product-info re-clamps the main qty input on variant change */
    subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
      syncQtyFromForm();
      setBtnLabel();
    });
    /* the add resolved (product-form publishes after /cart/add.js succeeds) */
    subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => {
      if (addPending && e && e.source === 'product-form') markAdded();
    });
    if (PUB_SUB_EVENTS.cartError) {
      subscribe(PUB_SUB_EVENTS.cartError, () => {
        if (addPending) restoreLabel();
      });
    }
  }

  /* ========================
     Bar -> form (through Dawn's variant pipeline)
     ======================== */
  function pushOptionsToForm(values) {
    const selects = variantSelects();
    if (!selects) return;
    /* bar-driven pick: the page must not move and focus must stay in the
       bar — cc-gallery-variant-sync's Dawn wrappers read this stamp to
       suppress the gallery scroll AND product-info's picker re-focus */
    document.body.dataset.ccBarPick = String(Date.now());
    const groups = selects.querySelectorAll('.product-form__input');
    values.forEach((value, i) => {
      const group = groups[i];
      if (!group) return;
      const dropdown = group.querySelector('select');
      if (dropdown) {
        if (dropdown.value !== value) {
          dropdown.value = value;
          dropdown.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      const radio = Array.from(group.querySelectorAll("input[type='radio']")).find((r) => r.value === value);
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  stickySelects.forEach((sel) => {
    sel.addEventListener('change', () => {
      let values = stickySelects.map((s) => s.value);
      if (!findVariantByOptions(values)) {
        /* an earlier option changed into a combo that doesn't exist — snap the
           LATER options to the first variant matching the new prefix,
           synchronously: Dawn never sees the impossible selection */
        const idx = parseInt(sel.getAttribute('data-cc-sticky-option'), 10);
        const prefix = values.slice(0, idx + 1);
        const cand = (data.variants || []).find((c) => prefix.every((o, q) => c.options[q] === o));
        if (cand) {
          values = cand.options.slice();
          stickySelects.forEach((s) => {
            const i2 = parseInt(s.getAttribute('data-cc-sticky-option'), 10);
            if (s.value !== values[i2]) s.value = values[i2];
          });
        }
      }
      pushOptionsToForm(values);
      /* immediate feedback; authoritative state re-arrives via variantChange */
      const picked = findVariantByOptions(values);
      updateStickyBtnState(picked);
      if (picked) setBtnLabel(picked);
    });
  });

  /* ========================
     Quantity controls (1..99, tightened by Dawn's quantity rules)
     ======================== */
  function qtyRules() {
    const f = formQty();
    let min = 1;
    let max = 99;
    let step = 1;
    if (f) {
      min = parseInt(f.min, 10) || 1;
      step = parseInt(f.step, 10) || 1;
      const fMax = parseInt(f.max, 10);
      if (fMax > 0) max = Math.min(fMax, 99);
    }
    return { min: min, max: max, step: step };
  }
  function clampQty(val) {
    const r = qtyRules();
    if (isNaN(val)) val = r.min;
    return Math.max(r.min, Math.min(r.max, val));
  }
  function syncQtyToForm() {
    const f = formQty();
    if (!f || !qtyInput) return;
    if (f.value !== qtyInput.value) {
      f.value = qtyInput.value;
      f.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  function syncQtyFromForm() {
    const f = formQty();
    if (!f || !qtyInput) return;
    if (f.value && f.value !== qtyInput.value) qtyInput.value = clampQty(parseInt(f.value, 10));
  }

  if (minusBtn && plusBtn && qtyInput) {
    minusBtn.addEventListener('click', () => {
      qtyInput.value = clampQty((parseInt(qtyInput.value, 10) || 1) - qtyRules().step);
      syncQtyToForm();
      setBtnLabel();
    });
    plusBtn.addEventListener('click', () => {
      qtyInput.value = clampQty((parseInt(qtyInput.value, 10) || 1) + qtyRules().step);
      syncQtyToForm();
      setBtnLabel();
    });
    qtyInput.addEventListener('change', () => {
      qtyInput.value = clampQty(parseInt(qtyInput.value, 10));
      syncQtyToForm();
      setBtnLabel();
    });
    qtyInput.addEventListener('input', () => setBtnLabel());
  }

  /* ========================
     Show/hide via IntersectionObserver (reliable during iOS rubber-band)
     ======================== */
  let isAtcInView = true;
  let isFooterInView = false;
  let atcRectBottom = 0;

  function updateVisibility() {
    let shouldShow;
    if (isFooterInView) {
      shouldShow = false;
    } else if (productSoldOut) {
      /* Sold-out products: always show (no usable main ATC to observe) */
      shouldShow = true;
    } else if (isDesktop()) {
      /* Desktop: show only after scrolling PAST the ATC button (above viewport) */
      shouldShow = atcRectBottom < 0;
    } else {
      /* Phone: show whenever the main ATC is not in view */
      shouldShow = !isAtcInView;
    }

    if (shouldShow && !isVisible) {
      bar.classList.add('is-visible');
      bar.setAttribute('aria-hidden', 'false');
      isVisible = true;
      syncQtyFromForm();
      setBtnLabel();
      hideThirdParty(true);
      trackEvent('cc_sticky_atc_shown');
    } else if (!shouldShow && isVisible) {
      bar.classList.remove('is-visible');
      bar.setAttribute('aria-hidden', 'true');
      isVisible = false;
      hideThirdParty(false);
      closePanel();
    }
  }

  const observeTarget = mainSubmit() || form || productInfo;
  const footerEl = document.querySelector('.footer, footer, .section-footer');

  if (typeof IntersectionObserver !== 'undefined') {
    new IntersectionObserver(
      (entries) => {
        const e = entries[entries.length - 1];
        isAtcInView = e.isIntersecting;
        atcRectBottom = e.boundingClientRect.bottom;
        updateVisibility();
      },
      { threshold: 0 }
    ).observe(observeTarget);

    if (footerEl) {
      new IntersectionObserver(
        (entries) => {
          isFooterInView = entries[entries.length - 1].isIntersecting;
          updateVisibility();
        },
        { threshold: 0 }
      ).observe(footerEl);
    }
  } else {
    /* Legacy fallback: window scroll + rect reads */
    let scrollTick = false;
    window.addEventListener(
      'scroll',
      () => {
        if (scrollTick) return;
        scrollTick = true;
        requestAnimationFrame(() => {
          const rect = observeTarget.getBoundingClientRect();
          const vh = window.innerHeight || document.documentElement.clientHeight;
          isAtcInView = rect.bottom > 0 && rect.top < vh;
          atcRectBottom = rect.bottom;
          isFooterInView = footerEl ? footerEl.getBoundingClientRect().top < vh : false;
          updateVisibility();
          scrollTick = false;
        });
      },
      { passive: true }
    );
  }

  /* ========================
     Variant chooser sheet (phone)
     ======================== */
  const imgToggles = bar.querySelectorAll('[data-cc-img-toggle]');
  if (imgToggles.length && panel) {
    imgToggles.forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation(); /* the chooser button sits inside the info toggle — one toggle per tap */
        if (isDesktop()) return; /* the selects own the choice on desktop */
        panelOpen ? closePanel() : openPanel();
      });
    });

    panel.querySelectorAll('[data-cc-panel-close]').forEach((el) => {
      el.addEventListener('click', closePanel);
    });

    panel.querySelectorAll('[data-cc-panel-variant]').forEach((tile) => {
      tile.addEventListener('click', () => {
        const v = variantById[tile.getAttribute('data-cc-panel-variant')];
        if (!v) return;
        pushOptionsToForm(v.options);
        /* optimistic feedback; authoritative state re-arrives via variantChange */
        updateStickyBtnState(v);
        setBtnLabel(v);
        if (stickyImg && (v.img || data.fallbackImg)) stickyImg.src = v.img || data.fallbackImg;
        if (variantEl) variantEl.textContent = v.title;
        highlightCurrentTile(String(v.id));
        trackEvent('cc_sticky_atc_variant_pick', { variant_id: v.id, variant_title: v.title });
        setTimeout(closePanel, 220);
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && panelOpen) closePanel();
    });
  }

  function openPanel() {
    if (!panel) return;
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    panelOpen = true;
    document.body.style.overflow = 'hidden';
    trackEvent('cc_sticky_atc_panel_open');
    highlightCurrentTile(currentVariantId());
  }

  function closePanel() {
    if (!panel || !panelOpen) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    panelOpen = false;
    document.body.style.overflow = '';
  }

  function highlightCurrentTile(vid) {
    if (!panel) return;
    panel.querySelectorAll('[data-cc-panel-variant]').forEach((t) => {
      t.classList.toggle('is-active', t.getAttribute('data-cc-panel-variant') === vid);
    });
  }

  /* ========================
     Reviews link: anchor to the Judge.me widget when the page has one,
     plain text otherwise (no dead links in the bar)
     ======================== */
  if (reviewsLink) {
    if (reviewsWord) reviewsWord.textContent = tr('reviews', 'reseñas');
    const widget = document.getElementById('judgeme_product_reviews') || document.querySelector('.jdgm-rev-widg');
    if (widget) {
      reviewsLink.addEventListener('click', (e) => {
        e.preventDefault();
        widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        trackEvent('cc_sticky_atc_reviews_click');
      });
    } else {
      reviewsLink.removeAttribute('href');
    }
  }

  /* ========================
     Hide chat + loyalty while the bar is up
     ======================== */
  function hideThirdParty(hide) {
    if (!document.getElementById('cc-sticky-hide-style')) {
      const style = document.createElement('style');
      style.id = 'cc-sticky-hide-style';
      style.textContent = [
        '.cc-sticky-active shopify-agent,',
        '.cc-sticky-active .chat-app,',
        '.cc-sticky-active [data-spec="toggle-button"],',
        '.cc-sticky-active #shopify-chat,',
        '.cc-sticky-active shopify-chat,',
        '.cc-sticky-active .shopify-inbox,',
        '.cc-sticky-active .lion-notification,',
        '.cc-sticky-active .lion-launcher,',
        '.cc-sticky-active [class*="loyaltylion"],',
        '.cc-sticky-active iframe[src*="inbox"],',
        '.cc-sticky-active iframe[src*="chat"] {',
        '  display: none !important;',
        '  visibility: hidden !important;',
        '  pointer-events: none !important;',
        '}',
      ].join('\n');
      document.head.appendChild(style);
    }
    document.body.classList.toggle('cc-sticky-active', hide);
  }

  /* ========================
     ATC button
     ======================== */
  function scrollToVariants() {
    const selects = variantSelects();
    if (!selects) return;
    selects.scrollIntoView({ behavior: 'smooth', block: 'center' });
    selects.querySelectorAll('.product-form__input').forEach((g) => {
      g.classList.add('cc-variant-highlight');
      setTimeout(() => g.classList.remove('cc-variant-highlight'), 2000);
    });
  }

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    if (addPending) return;
    const submit = mainSubmit();
    if (!submit) return;
    if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') {
      /* Unavailable combo (Dawn disables the main submit): scroll + flash the picker */
      scrollToVariants();
      return;
    }
    syncQtyToForm();
    beginAdding();
    submit.click(); /* Dawn's product-form owns the add; drawer/toast own post-ATC UX */
    trackEvent('cc_sticky_atc_click', {
      product: bar.querySelector('.cc-sticky-atc__title') ? bar.querySelector('.cc-sticky-atc__title').textContent : '',
      quantity: currentQty(),
      layout: isDesktop() ? 'desktop' : 'phone',
    });
  });

  /* ========================
     Hide bar while the phone keyboard is open (input focused outside bar)
     ======================== */
  function isExternalInput(el) {
    return (
      el &&
      !bar.contains(el) &&
      /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) &&
      !/^(button|submit|reset|checkbox|radio)$/i.test(el.type || '')
    );
  }
  document.addEventListener('focusin', () => {
    if (!isDesktop() && isExternalInput(document.activeElement)) {
      bar.style.display = 'none';
    }
  });
  document.addEventListener('focusout', () => {
    setTimeout(() => {
      if (!isExternalInput(document.activeElement)) {
        bar.style.display = '';
      }
    }, 100);
  });

  /* ========================
     Init
     ======================== */
  if (btnVerb) btnVerb.textContent = tr('Add', 'Agregar');
  if (btnLong) btnLong.textContent = tr(' to cart', ' al carrito');
  if (productSoldOut) {
    /* Delay slightly so Stoq has time to inject its button */
    setTimeout(showNotifyMode, 1000);
    setBtnLabel();
  } else {
    resyncFromDom();
  }
  filterStickyOptions(currentVariant()); /* sold-out init skips resync/applyVariant */
  setBtnLabel();
  updateVisibility();
})();
